import { atom, } from 'jotai';
import React, { useEffect, useMemo, useState } from 'react';
import ReviewToolbar from './ManageRestaurant/ReviewToolbar';
import { getReviewColumns } from './ManageRestaurant/ReviewColumns';
import { useLocation } from 'react-router-dom';
import { useReviewManagementParams } from './useReviewManagementParams';
import ReviewViewDialog from './ManageRestaurant/ReviewViewDialog';
import { useDebounce } from '../../../utils/useDounce';
import { useSnackbar } from '../../stateless/SnackBarProvider';
import { useAllItemReview, useDeleteReview, useReviewApprove } from '../../Query/ItemReviewQuery';
import { DeliveryBoyReview } from '../../../client-sdk';
import CommonDataGrid from '../../stateless/CommonDataGrid';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';


export interface ChoiceGroupDataType {
    storeType: any;
    choiceGroupNameEnglish: string;
    choiceGroupNameArabic: string;
    minimumLimit: number | null;
    maximumLimit: number | null;
    choiceDetails: any;
    choiceGroupOption: any;
    merchantDetails: any;
    id: any;
}

export const choiceGroupAtom = atom<ChoiceGroupDataType | null>(null);

const ManageOrderReview = () => {
    const { t, i18n } = useTranslation();
    const [, forceUpdate] = useState({});

    // Force re-render when language changes
    useEffect(() => {
        const handleLanguageChange = () => {
            forceUpdate({});
        };

        i18n.on('languageChanged', handleLanguageChange);
        
        return () => {
            i18n.off('languageChanged', handleLanguageChange);
        };
    }, [i18n]);

    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 400);

    const { params, dispatch } = useReviewManagementParams();
    const isReady = Boolean(params && Object.keys(params).length > 0);
    const [selectedReviewIds, setSelectedReviewIds] = useState<string[]>([]);
    const [currentReview, setCurrentReview] = useState<any>(null);
    const { showSnackbar } = useSnackbar();
    const { showMessage } = useSnackbar();
    const location = useLocation();
    const reviewType = location.state?.reviewType;

    const memoizedParams = useMemo(() => {
        if (!isReady) return {};

        return {
            ...params,
            type: reviewType || params.type // 🔁 use reviewType from location.state if present
        };
    }, [isReady, params, reviewType]);


    const { data: ItemReviewData, isLoading, refetch } = useAllItemReview(memoizedParams);

    const filteredRows = React.useMemo(() => {
        const storeOwners = ItemReviewData?.data || [];

        // Add sno field to each row and ensure all required fields are present
        return storeOwners.map((row, index) => {
            // Ensure we have a valid row object
            if (!row) return

            // Create a new object with all the row properties plus sno
            const rowWithSno = {
                ...row,
                id: (row as DeliveryBoyReview).reviewId, // type assertion here
                sno: index + 1 + (Number(params.page - 1)) * Number(params.limit),
                deliveryPersonName: (row as DeliveryBoyReview).deliveryPersonName || "N/A",
            };

            return rowWithSno;
        });
    }, [params.search, ItemReviewData, params.page, params.limit]);

    const { mutate: toggleStatus } = useReviewApprove();
    const { mutate: deleteReview } = useDeleteReview();

    const handleCloseDialog = () => setCurrentReview(null);


    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value)
    };

    useEffect(() => {
        dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
    }, [debouncedSearch]);


    const handleSelectionModelChange = (selection: any[]) =>
        setSelectedReviewIds(selection.map(String));

    const handlePageChange = (newPage: number) => {
        dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
    };

    const handlePageSizeChange = (newSize: number) => {
        dispatch({ type: 'SET_LIMIT', payload: newSize });
    };

    const handleEditClick = (row: any) => setCurrentReview(row);

    const handleToggleStatus = (id: string[], isApproved: boolean) => {
        const message = isApproved 
            ? t('review.orderReview.messages.confirmApprove')
            : t('review.orderReview.messages.confirmDisapprove');
        const confirmed = window.confirm(message);
        if (!confirmed) return;
        toggleStatus(
            { id, data: { status: isApproved ? true : false } },
            {
                onSuccess: () => {
                    refetch();
                },
                onError: () => showSnackbar(t('review.orderReview.messages.failedUpdate'), 'error'),
            }
        );
    };

    const handleDeleteButton = (ids: string[]) => {
        const confirmed = window.confirm(t('review.orderReview.messages.confirmDelete'));
        if (!confirmed) return;
        deleteReview(ids, {
            onSuccess: () => {
                setSelectedReviewIds([]);
                refetch();
            },
            onError: () => showSnackbar(t('review.orderReview.messages.failedDelete'), 'error'),
        });
    };

    const handleBulkAction = (action: 'approve' | 'disapprove' | 'delete') => {
        if (selectedReviewIds.length === 0) {
            showMessage(t('review.orderReview.messages.selectOne'), 'error');
            return;
        }
        if (action === "approve") {
            handleToggleStatus(selectedReviewIds, true)
        } else if (action === "disapprove") {
            handleToggleStatus(selectedReviewIds, false)
        } else if (action === "delete") {
            handleDeleteButton(selectedReviewIds)
        }

    };

    const columns = getReviewColumns({
        onEdit: handleEditClick,
        onDelete: handleDeleteButton,
        onDisapprove: (id: string[]) => handleToggleStatus(id, false),
        onApprove: (id: string[]) => handleToggleStatus(id, true),
        fields: {
            sno: 'sno',
            deliveryPersonName: "deliveryPersonName",
            customerName: "customerName",
            view: 'View',
            status: 'Status',
            delete: 'delete',
        },
        translationKey: 'review.orderReview',
    });

    return (
        <>
            <Typography variant="h5" fontWeight="bold" mb={2}>
                {t('review.orderReview.title')}
            </Typography>
            <ReviewToolbar
                searchTerm={searchTerm}
                onSearchChange={handleSearchChange}
                onDisapprove={() => handleBulkAction('disapprove')}
                onApprove={() => handleBulkAction('approve')}
                onDelete={() => handleBulkAction('delete')}
                showApprove={selectedReviewIds.length > 0}
                showDisapprove={selectedReviewIds.length > 0}
                showDelete={selectedReviewIds.length > 0}
                searchPlaceholderKey="review.orderReview.searchPlaceholder"
            />

            <CommonDataGrid
                key={`datagrid-page-${params.page}-limit-${params.limit}`}
                rows={filteredRows || []}
                columns={columns}
                pageSize={params.limit}
                onPageSizeChange={handlePageSizeChange}
                page={params.page - 1}
                onPageChange={handlePageChange}
                checkboxSelection
                loading={isLoading}
                rowCount={ItemReviewData?.pagination?.totalItems || 0}
                selectionModel={selectedReviewIds}
                onRowSelectionModelChange={handleSelectionModelChange} components={undefined} />

            <ReviewViewDialog currentReview={currentReview} onClose={handleCloseDialog} />
        </>
    );
};

export default ManageOrderReview;
